/**
 * Phone water readings for one pond/date: slot tabs, the fields that slot
 * measures, and the in-app keypad pinned below the list. Fields are buttons,
 * never inputs, so the phone keyboard can't open. A typed value commits when
 * the field is left (Next, another field, another slot, or leaving the page).
 *
 * A slot opens on its first empty field test; when every field test is filled no
 * field is active and the keypad stays inert until one is tapped. The active
 * field's saved value is shown selected: the first key typed replaces it.
 */
import { useWaterSlotRules } from '@/hooks/useWaterSlotRules';
import { useFarmThresholds } from '@/hooks/useFarmThresholds';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/cn';
import { PAIR_EXAMPLE, pairRefusedMessage, alkalinityTier, calMagTier, formatPair, pairChanges, parsePairInput } from '@/lib/pairs';
import { displayPhDate, todayInPH } from '@/lib/phDate';
import { waterColorTier } from '@/lib/waterColor';
import { CLEAR_NO_DEPTH_ROW, clearDepthCm, clearText } from '@/lib/clearToBottom';
import {
  ALL_SLOTS,
  CLEAR_TO_BOTTOM,
  SLOT_META,
  GRID_PARAMS,
  GRID_ROWS,
  OBSERVATION_PARAMS,
  dbColumnFor,
  decimalLimitHint,
  fieldParamsFor,
  fieldsFor,
  isClearToBottom,
  isFarmOff,
  notMeasuredSentence,
  tierFor,
  ownerParam,
  paramByKey,
  refusedValueMessage,
  roundToColumnScale,
} from '@/lib/waterModel';
import type { ParamDef, Tier } from '@/lib/waterModel';
import type { ReadingSlot } from '@/types/entry';
import { Keypad } from '@/components/entry/Keypad';
import { PhoneMenuButton } from '@/components/layout/PhoneMenuButton';
import { SignInAgainButton } from '@/components/layout/SignInAgainButton';
import type { DayReadings } from '@/components/entry/adaptive/entryData';
import { cellKey, type CellVal, useReadingAutosave } from '@/components/entry/adaptive/useReadingAutosave';

interface Props {
  farmId: string;
  cycleId: string;
  pondId: string;
  pondName: string;
  doc: number | null;
  date: string;
  dayReadings: DayReadings;
  initialSlot: ReadingSlot;
  onSaved: () => void;
}

const MAX_DRAFT_LENGTH = 7;
// "123456/123456": two whole numbers and the slash.
const MAX_PAIR_DRAFT_LENGTH = 13;
// After the server's refusal puts the tech back on a field, keys tapped while
// their eyes were still on the next field must not land in it.
const REFOCUS_GUARD_MS = 600;
const DECIMAL_HINT_MS = 2500;

const isBlank = (v: CellVal | undefined) => v === null || v === undefined || v === '';

const pairText = (a: CellVal | undefined, b: CellVal | undefined): string =>
  formatPair(typeof a === 'number' ? a : null, typeof b === 'number' ? b : null) ?? '';

/** The two numbers in a pair's text ("100/110", "100", "—/110"); a side that is blank or a dash is null. */
const pairNumbers = (text: string): [number | null, number | null] => {
  const [a, b] = text.split('/').map((t) => (/^\d+$/.test(t.trim()) ? Number(t.trim()) : null));
  return [a ?? null, b ?? null];
};

// With a param key the value is rounded to what its column keeps, so it is what the database will hold.
function parseDraft(text: string, key?: string): number | null {
  const t = text.trim();
  if (t === '' || t === '.') return null;
  const n = parseFloat(t);
  if (!Number.isFinite(n)) return null;
  return key ? roundToColumnScale(key, n) : n;
}

// A clear reading stands at the same slot's saved depth, for display only (#396); a refused depth is not saved.
function shownText(p: ParamDef, v: CellVal, savedDepth: CellVal | undefined): string {
  if (isBlank(v)) return '';
  if (isClearToBottom(v)) return clearDepthCm(savedDepth) === null ? CLEAR_NO_DEPTH_ROW : clearText(savedDepth, { unit: false });
  if (p.type === 'select') return p.options?.find((o) => o.value === v)?.label ?? String(v);
  return String(v);
}

/** The reading columns a field edits: its own, plus the second half when it is a pair. */
const columnsOf = (key: string): string[] => {
  const p = paramByKey(key);
  const second = p?.pairSecondKey ? paramByKey(p.pairSecondKey) : undefined;
  return [dbColumnFor(key), ...(second ? [second.dbColumn] : [])];
};
const paramKeyOfColumn = (column: string): string => {
  const p = [...GRID_PARAMS, ...OBSERVATION_PARAMS].find((x) => x.dbColumn === column);
  return p?.key ?? column;
};

const tierField: Record<Tier, string> = {
  ok: 'border-gray-200',
  warning: 'bg-amber-50 border-amber-300',
  critical: 'bg-red-50 border-red-300',
};
const tierValue: Record<Tier, string> = {
  ok: 'text-gray-900',
  warning: 'text-amber-800',
  critical: 'text-red-700',
};

export function PhoneWater({
  farmId,
  cycleId,
  pondId,
  pondName,
  doc,
  date,
  dayReadings,
  initialSlot,
  onSaved,
}: Props) {
  const { table, colorTiers } = useFarmThresholds(farmId);
  const slotOff = useWaterSlotRules(farmId);
  // The server's refusal jumps back to the refused field, so it is active again.
  const refusedRef = useRef<(s: ReadingSlot, cols: string[], values: Record<string, CellVal>) => void>(() => {});
  const { display, commitCell, retrySlot, rejected, failed, slotSaving, slotQueued, savedCount, signedOut } = useReadingAutosave({
    farmId,
    cycleId,
    date,
    dayReadings,
    onSaved,
    retry: 'manual',
    onRejected: (s, cols, values) => refusedRef.current(s, cols, values),
  });

  const [slot, setSlot] = useState<ReadingSlot>(initialSlot);
  const [activeKey, setActiveKey] = useState<string | null>(() => {
    const first = fieldParamsFor(initialSlot, slotOff).find((p) => isBlank(display(initialSlot, p.key)));
    return first?.key ?? null;
  });
  // null = untouched (shows the saved value; the first key typed replaces it).
  const [draft, setDraft] = useState<string | null>(null);
  const [refusedValues, setRefusedValues] = useState<Record<string, CellVal>>({});

  // The refusal handler runs from an async save, so it reads these, never a stale render.
  const latest = useRef({ draft, slot });
  useLayoutEffect(() => {
    latest.current = { draft, slot };
  });
  // Refused fields the tech hasn't been taken to yet (marked, not yet visited).
  const pendingRef = useRef<{ slot: ReadingSlot; key: string }[]>([]);
  const guardUntil = useRef(0);
  const guardTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [guardedKey, setGuardedKey] = useState<string | null>(null);
  useEffect(() => () => clearTimeout(guardTimer.current), []);
  const guardKeypad = (key: string) => {
    guardUntil.current = Date.now() + REFOCUS_GUARD_MS;
    setGuardedKey(key);
    clearTimeout(guardTimer.current);
    guardTimer.current = setTimeout(() => {
      guardUntil.current = 0;
      setGuardedKey(null);
    }, REFOCUS_GUARD_MS);
  };
  const keypadGuarded = () => Date.now() < guardUntil.current;

  // The hint a refused "." or digit shows under its field, for a moment.
  const [keyHint, setKeyHint] = useState<{ key: string; text: string } | null>(null);
  const keyHintTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Set when a "." was refused: every digit after it is refused too, until the tech edits or leaves the
  // field. Dropping only the "." would let "4 5 . 5" on a whole-number column read 455 (#245).
  const fractionRefused = useRef<string | null>(null);
  useEffect(() => () => clearTimeout(keyHintTimer.current), []);
  const clearKeyHint = () => {
    clearTimeout(keyHintTimer.current);
    setKeyHint(null);
  };
  const resetKeyRefusal = () => {
    fractionRefused.current = null;
    clearKeyHint();
  };
  const refuseKey = (key: string, text: string, holdFraction: boolean) => {
    if (holdFraction) fractionRefused.current = text;
    setKeyHint({ key, text });
    clearTimeout(keyHintTimer.current);
    keyHintTimer.current = setTimeout(() => setKeyHint(null), DECIMAL_HINT_MS);
  };

  const fieldRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const fields = fieldsFor(slot, slotOff);
  const notMeasured = [...GRID_ROWS, ...OBSERVATION_PARAMS].filter((p) => isFarmOff(p.key, slot, slotOff));
  const active = activeKey ? (paramByKey(activeKey) ?? null) : null;

  const commitDraft = useCallback(
    (s: ReadingSlot, key: string, text: string | null) => {
      // Typing replaces the value, so a stray "." must not wipe a saved reading.
      if (text === null || text.trim() === '.') return;
      const param = paramByKey(key);
      if (param?.pairSecondKey) {
        const second = param.pairSecondKey;
        if (text.trim() === pairText(display(s, key), display(s, second))) return;
        const input = parsePairInput(text);
        if ('error' in input) return; // a lone "/": nothing typed
        // What is typed is the whole cell: a side left out is cleared, never kept out of sight.
        const whole = { first: input.first ?? 'clear', second: input.second ?? 'clear' };
        const refusedBefore = [key, second].some((k) => rejected.has(cellKey(s, dbColumnFor(k))));
        const changes = pairChanges(whole, { first: display(s, key), second: display(s, second) }, refusedBefore);
        if (changes.first !== undefined) commitCell(s, key, changes.first);
        if (changes.second !== undefined) commitCell(s, second, changes.second);
        return;
      }
      const next = parseDraft(text, key);
      const cur = display(s, key);
      if (next === (typeof cur === 'number' || isClearToBottom(cur) ? cur : null)) return;
      commitCell(s, key, next);
    },
    [display, commitCell, rejected]
  );

  // A value still being typed when the page goes away (back, nav, phone locked,
  // tab closed) is committed and sent at once rather than after the debounce.
  const saveTypedNow = () => {
    if (active) commitDraft(slot, active.key, draft);
    ALL_SLOTS.forEach(retrySlot);
  };
  const saveTypedNowRef = useRef(saveTypedNow);
  useEffect(() => {
    saveTypedNowRef.current = saveTypedNow;
  });
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') saveTypedNowRef.current();
    };
    const onPageHide = () => saveTypedNowRef.current();
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
      saveTypedNowRef.current();
    };
  }, []);

  // Back on the network: what failed while offline is sent once, without a tap. A slot that fails
  // again waits for the tech as before (no loop); a dead session is not retried.
  const retryFailedRef = useRef<() => void>(() => {});
  useEffect(() => {
    retryFailedRef.current = () => {
      if (signedOut) return;
      ALL_SLOTS.filter((s) => [...failed].some((k) => k.startsWith(`${s}|`))).forEach(retrySlot);
    };
  });
  useEffect(() => {
    const onOnline = () => retryFailedRef.current();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  useEffect(() => {
    if (activeKey) fieldRefs.current.get(activeKey)?.scrollIntoView({ block: 'nearest' });
  }, [slot, activeKey]);

  const goTo = (s: ReadingSlot, key: string | null) => {
    // The async refusal handler reads this before the next render commits.
    latest.current = { draft: null, slot: s };
    // Any navigation ends the post-refusal guard: a digit after a deliberate Next/Skip is for the new field.
    guardUntil.current = 0;
    clearTimeout(guardTimer.current);
    setGuardedKey(null);
    resetKeyRefusal();
    setDraft(null);
    setSlot(s);
    setActiveKey(key);
  };

  // The 400 lands ~1 s after Next, usually while the tech is already typing the
  // next field. Focus, slot and draft are never taken from under them: the refused
  // field is only marked and remembered, and the tech is brought to it by their
  // next Next / Skip / field tap / tab tap. With nothing typed, on the slot the
  // refusal belongs to, they are taken there at once.
  useEffect(() => {
    refusedRef.current = (s, cols, values) => {
      const refused = fieldsFor(s, slotOff).filter((p) => cols.some((c) => ownerParam(c)?.key === p.key));
      if (refused.length === 0) return;
      setRefusedValues((prev) => {
        const next = { ...prev };
        for (const [c, v] of Object.entries(values)) next[cellKey(s, c)] = v;
        return next;
      });
      const marked = refused.map((p) => ({ slot: s, key: p.key }));
      const queued = pendingRef.current.filter((r) => !marked.some((m) => m.slot === r.slot && m.key === r.key));
      const idle = s === latest.current.slot && latest.current.draft === null;
      pendingRef.current = idle ? [...queued, ...marked.slice(1)] : [...queued, ...marked];
      if (idle) {
        goTo(s, marked[0].key);
        guardKeypad(marked[0].key);
      }
    };
  });

  const livePending = () =>
    pendingRef.current.filter(
      (r) => columnsOf(r.key).some((c) => rejected.has(cellKey(r.slot, c))) && !(r.slot === slot && r.key === activeKey)
    );

  const visit = (r: { slot: ReadingSlot; key: string }) => {
    pendingRef.current = pendingRef.current.filter((x) => !(x.slot === r.slot && x.key === r.key));
    goTo(r.slot, r.key);
  };

  const draftFilled = (p: ParamDef, text: string): boolean => {
    if (!p.pairSecondKey) return parseDraft(text, p.key) !== null;
    const input = parsePairInput(text);
    return !('error' in input) && (typeof input.first === 'number' || typeof input.second === 'number');
  };

  const needsValue = (s: ReadingSlot, p: ParamDef, typed?: { key: string; filled: boolean }) => {
    if (columnsOf(p.key).some((c) => failed.has(cellKey(s, c)) || rejected.has(cellKey(s, c)))) return true;
    if (typed && typed.key === p.key) return !typed.filled;
    return columnsOf(p.key).every((c) => isBlank(display(s, paramKeyOfColumn(c))));
  };

  // The draft is committed on leaving the field, so it counts as filled here.
  const typedEdit = (s: ReadingSlot) =>
    active && s === slot && active.type === 'number' && draft !== null && draft.trim() !== '.'
      ? { key: active.key, filled: draftFilled(active, draft) }
      : undefined;

  const firstEmptyKey = (s: ReadingSlot): string | null =>
    fieldParamsFor(s, slotOff).find((p) => needsValue(s, p, typedEdit(s)))?.key ?? null;

  const advance = () => {
    if (!active) return;
    const pending = livePending();
    const here = pending.find((r) => r.slot === slot);
    if (here) {
      visit(here);
      return;
    }
    const idx = fields.findIndex((p) => p.key === active.key);
    if (idx >= 0 && idx < fields.length - 1) {
      goTo(slot, fields[idx + 1].key);
      return;
    }
    if (pending.length > 0) {
      visit(pending[0]);
      return;
    }
    const si = ALL_SLOTS.indexOf(slot);
    if (si < ALL_SLOTS.length - 1) {
      // Next keeps walking: a filled slot still opens on its first field rather than nothing.
      const ns = ALL_SLOTS[si + 1];
      goTo(ns, firstEmptyKey(ns) ?? fieldsFor(ns, slotOff)[0].key);
    } else {
      setDraft(null);
    }
  };

  const onNext = () => {
    if (!active) return;
    resetKeyRefusal();
    commitDraft(slot, active.key, draft);
    advance();
  };

  const onSkip = () => {
    if (!active) return;
    resetKeyRefusal();
    setDraft(null);
    advance();
  };

  const selectField = (s: ReadingSlot, key: string) => {
    if (s === slot && key === activeKey) return;
    if (active) commitDraft(slot, active.key, draft);
    const here = livePending().find((r) => r.slot === s && r.key !== key);
    if (here) {
      visit(here);
      return;
    }
    pendingRef.current = pendingRef.current.filter((r) => !(r.slot === s && r.key === key));
    goTo(s, key);
  };

  const openSlot = (s: ReadingSlot) => {
    if (active) commitDraft(slot, active.key, draft);
    const refused = livePending().find((r) => r.slot === s);
    if (refused) {
      visit(refused);
      return;
    }
    goTo(s, firstEmptyKey(s));
  };

  const onDigit = (c: string) => {
    if (!active || active.type !== 'number' || keypadGuarded()) return;
    const base = draft ?? '';
    const decimals = active.decimals;
    const limit = decimalLimitHint(active.key) ?? '';
    if (fractionRefused.current !== null) {
      refuseKey(active.key, fractionRefused.current, false);
      return;
    }
    let next: string;
    if (c === '/') {
      if (!active.pairSecondKey) return;
      if (base.includes('/')) {
        refuseKey(active.key, 'Only one / between the two numbers', false);
        return;
      }
      next = `${base}/`;
    } else if (c === '.') {
      if (decimals === 0) {
        refuseKey(active.key, limit, true);
        return;
      }
      if (base.includes('.')) {
        refuseKey(active.key, 'Only one decimal point', true);
        return;
      }
      next = `${base}.`;
    } else {
      next = base === '0' ? c : base + c;
      const fraction = next.split('.')[1];
      if (decimals !== undefined && fraction !== undefined && fraction.length > decimals) {
        refuseKey(active.key, limit, false);
        return;
      }
    }
    if (next.length > (active.pairSecondKey ? MAX_PAIR_DRAFT_LENGTH : MAX_DRAFT_LENGTH)) return;
    // Each half of a pair is a whole number of at most 6 digits ("320/1094").
    if (active.pairSecondKey && next.split('/').some((half) => half.length > 6)) return;
    clearKeyHint();
    setDraft(next);
  };

  const onDelete = () => {
    if (!active || active.type !== 'number' || keypadGuarded()) return;
    resetKeyRefusal();
    // An untouched saved value is "selected": Delete clears all of it, as typing would replace it.
    setDraft(draft === null ? '' : draft.slice(0, -1));
  };

  const onClear = () => {
    if (!active || keypadGuarded()) return;
    resetKeyRefusal();
    if (active.type === 'select') {
      if (!isBlank(display(slot, active.key))) commitCell(slot, active.key, null);
      return;
    }
    setDraft('');
  };

  const pick = (value: string) => {
    if (active && display(slot, active.key) !== value) commitCell(slot, active.key, value);
  };

  const valueOf = (p: ParamDef): string =>
    p.key === activeKey && draft !== null
      ? draft
      : p.pairSecondKey
        ? pairText(display(slot, p.key), display(slot, p.pairSecondKey))
        : shownText(p, display(slot, p.key), dayReadings[slot]?.values[dbColumnFor('depth')]);

  const typedUnsaved =
    !!active &&
    active.type === 'number' &&
    draft !== null &&
    draft.trim() !== '.' &&
    (active.pairSecondKey
      ? draft.trim() !== pairText(display(slot, active.key), display(slot, active.pairSecondKey))
      : parseDraft(draft, active.key) !== (display(slot, active.key) ?? null));
  const slotPending = (s: ReadingSlot) => !!(slotSaving[s] || slotQueued[s]);
  const slotUnsettled = (s: ReadingSlot) =>
    [...failed, ...rejected].some((k) => k.startsWith(`${s}|`));

  // Same rule as the server's completed_slots (field tests only, lab optional),
  // but only once the slot has fully settled: nothing typed, queued, saving,
  // failed or refused, so a ✓ never runs ahead of what the server holds.
  const slotDone = (s: ReadingSlot) =>
    fieldParamsFor(s, slotOff).every((p) => !isBlank(display(s, p.key))) &&
    !slotPending(s) &&
    !slotUnsettled(s) &&
    !(typedUnsaved && s === slot);
  const counted = fieldParamsFor(slot, slotOff);
  const filledCount = counted.filter((p) => !needsValue(slot, p)).length;

  const anySaving = ALL_SLOTS.some(slotPending);
  const failedSlots = ALL_SLOTS.filter((s) => [...failed].some((k) => k.startsWith(`${s}|`)));
  const flaggedSlots = ALL_SLOTS.filter(slotUnsettled);
  const refusedElsewhere = [...rejected]
    .map((k) => k.split('|') as [ReadingSlot, string])
    .filter(([s]) => s !== slot)
    .map(([s, col]) => ({ slot: s, param: fieldsFor(s, slotOff).find((p) => p.key === ownerParam(col)?.key) }))
    .find((r) => r.param);
  // Values that leaving the screen would lose: failed ones are only retried in the
  // background, refused ones are gone. Typed and queued values are flushed on leaving.
  const leaveLosesValues = failed.size > 0 || rejected.size > 0;
  const unsaved = typedUnsaved || anySaving || failed.size > 0 || rejected.size > 0;
  const saveState = ((): { label: string; cls: string } => {
    if (failed.size > 0) return { label: 'Not saved', cls: 'bg-red-100 text-red-800' };
    if (anySaving) return { label: 'Saving…', cls: 'bg-gray-100 text-gray-600' };
    if (rejected.size > 0) return { label: 'Check values', cls: 'bg-amber-100 text-amber-800' };
    if (typedUnsaved) return { label: 'Not saved yet', cls: 'bg-gray-100 text-gray-600' };
    if (savedCount > 0) return { label: 'Saved', cls: 'bg-emerald-100 text-emerald-800' };
    return { label: '', cls: '' };
  })();

  // Lets the browser ask before a tab close / reload throws away what hasn't landed.
  useEffect(() => {
    if (!unsaved) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsaved]);

  const activeText = active ? valueOf(active) : '';
  const announcement = active
    ? `${active.label} at ${SLOT_META[slot].label}: ${activeText === '' ? 'blank' : activeText}`
    : `${SLOT_META[slot].label} readings complete. Tap a field to change a value.`;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-100 short-landscape:flex-row" data-phone-water>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="shrink-0 space-y-2 border-b border-gray-200 bg-white px-4 pb-3 pt-2">
          <div className="flex items-center gap-2">
            <Link
              to="/entry"
              search={{}}
              aria-label="Back to Today"
              onClick={(e) => {
                if (leaveLosesValues && !window.confirm('Some values are not saved. Leave anyway?')) e.preventDefault();
              }}
              className="-ml-2 flex min-h-11 min-w-11 items-center justify-center rounded-lg text-gray-900 active:bg-gray-100"
            >
              <ChevronLeft className="h-6 w-6" aria-hidden="true" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-[17px] font-bold text-gray-900">{pondName} · Water readings</h1>
              {/* The save chip sits on the date line, so it never squeezes the title; -my keeps the line's height. */}
              <div className="flex items-start justify-between gap-2">
                <p className="min-w-0 text-xs text-gray-500">
                  {doc !== null && `DOC ${doc} · `}
                  {date < todayInPH() ? 'Entering for ' : ''}
                  {displayPhDate(date, { weekday: 'short', month: 'short', day: 'numeric' })}
                  {date < todayInPH() && <span className="ml-1.5 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-800" data-past-day>Past day</span>}
                </p>
                <span
                  role="status"
                  data-save-state
                  className={cn('-my-1 shrink-0 rounded-full text-xs font-semibold', saveState.label && 'px-2.5 py-1', saveState.cls)}
                >
                  {saveState.label}
                </span>
              </div>
            </div>
            <PhoneMenuButton confirmLeave={leaveLosesValues} />
          </div>
          <div className="grid grid-cols-5 gap-1.5" role="tablist" aria-label="Reading time">
            {ALL_SLOTS.map((s) => {
              const done = slotDone(s);
              const on = s === slot;
              const unsaved = flaggedSlots.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  data-slot={s}
                  onClick={() => openSlot(s)}
                  className={cn(
                    'min-h-11 min-w-11 rounded-lg border text-[13px] font-semibold touch-manipulation',
                    on
                      ? 'border-primary-600 bg-primary-600 text-white'
                      : done
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                        : 'border-gray-300 bg-white text-gray-700',
                    unsaved && 'ring-2 ring-red-500'
                  )}
                >
                  {SLOT_META[s].label}
                  {done && (
                    <>
                      <span aria-hidden="true"> ✓</span>
                      <span className="sr-only">, done</span>
                    </>
                  )}
                  {unsaved && <span className="sr-only">, has a value to check</span>}
                </button>
              );
            })}
          </div>
          {refusedElsewhere?.param && (
            <p role="alert" data-refusal-banner className="rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
              {refusedElsewhere.param.label} at {SLOT_META[refusedElsewhere.slot].label} isn't accepted. Tap{' '}
              {SLOT_META[refusedElsewhere.slot].label} to fix it.
            </p>
          )}
          {signedOut && (
            <div
              role="alert"
              data-signed-out
              className="flex items-center justify-between gap-3 rounded-lg bg-red-50 px-3 py-1.5 text-sm font-medium text-red-800"
            >
              <span>Signed out. Values not saved yet will be lost.</span>
              <SignInAgainButton className="shrink-0" />
            </div>
          )}
          {failedSlots.length > 0 && !signedOut && (
            <button
              type="button"
              onClick={() => failedSlots.forEach((s) => retrySlot(s))}
              className="flex min-h-11 w-full items-center justify-center rounded-lg bg-red-50 px-3 text-sm font-semibold text-red-800"
              data-retry-all
            >
              Some readings not saved · Try again
            </button>
          )}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3" data-field-list>
          <div className="space-y-2">
            {fields.map((p) => {
              const isActive = p.key === activeKey;
              const text = valueOf(p);
              const pairNums = p.pairSecondKey ? pairNumbers(text) : null;
              const tier = pairNums
                ? p.key === 'alkalinity'
                  ? alkalinityTier(table, pairNums[0], pairNums[1], { salinity_ppt: display(slot, 'salinity') })
                  : calMagTier(table, pairNums[0], pairNums[1], { salinity_ppt: display(slot, 'salinity') })
                : p.type === 'number'
                  ? tierFor(table, p.key, parseDraft(text, p.key), { salinity_ppt: display(slot, 'salinity') })
                  : p.key === 'waterColor'
                    ? (waterColorTier(colorTiers, String(display(slot, p.key) ?? '')) ?? 'ok')
                    : 'ok';
              const key = cellKey(slot, dbColumnFor(p.key));
              const cols = columnsOf(p.key).map((c) => cellKey(slot, c));
              const isFailed = cols.some((k) => failed.has(k));
              const isRejected = cols.some((k) => rejected.has(k));
              // A pair's withheld half is flagged too but has no refused value: the sentence belongs to the refused one.
              const refusedCol = cols.find((k) => rejected.has(k) && refusedValues[k] !== undefined) ?? cols.find((k) => rejected.has(k));
              const refusedNote =
                isRejected && !isFailed && refusedCol && text !== ''
                  ? p.pairSecondKey
                    ? refusedValues[refusedCol] !== undefined && pairNums?.[refusedCol === key ? 0 : 1] === Number(refusedValues[refusedCol])
                      ? pairRefusedMessage(table, p.key, refusedCol === key ? p.key : p.pairSecondKey, String(refusedValues[refusedCol]))
                      : null
                    : String(refusedValues[key]) === text
                      ? refusedValueMessage(table, p.key, text)
                      : null
                  : null;
              const replaceable = isActive && p.type === 'number' && draft === null && text !== '';
              return (
                <div
                  key={p.key}
                  ref={(el) => {
                    if (el) fieldRefs.current.set(p.key, el);
                    else fieldRefs.current.delete(p.key);
                  }}
                  className="scroll-my-3"
                  data-field={p.key}
                  data-active={isActive || undefined}
                  data-refocus-guard={guardedKey === p.key || undefined}
                >
                  <button
                    type="button"
                    aria-current={isActive || undefined}
                    aria-label={`${p.label}${p.unit ? ` (${p.unit})` : ''}: ${text === '' ? 'blank' : text}`}
                    onClick={() => selectField(slot, p.key)}
                    className={cn(
                      'flex min-h-[52px] w-full items-center justify-between gap-3 rounded-xl border bg-white px-3.5 text-left touch-manipulation',
                      tierField[tier],
                      isActive && 'border-2 border-primary-600',
                      isActive && tier === 'ok' && 'bg-primary-50',
                      (isFailed || isRejected) && 'border-red-400',
                      guardedKey === p.key && 'animate-pulse ring-4 ring-red-300'
                    )}
                  >
                    <span className="text-[15px] text-gray-900">
                      {p.label}
                      {p.unit && <span className="ml-1 text-[13px] text-gray-500">{p.unit}</span>}
                    </span>
                    <span
                      className={cn(
                        'flex items-center rounded-md text-xl font-bold tabular-nums',
                        text === '' ? 'text-gray-400' : tierValue[tier],
                        p.type === 'select' && 'text-base',
                        replaceable && 'bg-primary-200 px-1.5'
                      )}
                      data-value
                      data-selected={replaceable || undefined}
                    >
                      {text === '' ? '—' : text}
                      {isActive && p.type === 'number' && !replaceable && (
                        <span className="ml-0.5 inline-block h-5 w-0.5 animate-pulse bg-primary-600" aria-hidden="true" />
                      )}
                    </span>
                  </button>
                  {isActive && p.type === 'select' && (
                    <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={p.label}>
                      {(p.options ?? []).map((o) => {
                        const chosen = display(slot, p.key) === o.value;
                        return (
                          <button
                            key={o.value}
                            type="button"
                            aria-pressed={chosen}
                            onClick={() => pick(o.value)}
                            className={cn(
                              'min-h-11 min-w-11 rounded-full border px-4 text-sm font-medium touch-manipulation',
                              chosen
                                ? 'border-primary-600 bg-primary-600 text-white'
                                : 'border-gray-300 bg-white text-gray-700'
                            )}
                          >
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {isActive && p.key === 'transparency' && (
                    <button
                      type="button"
                      data-clear-to-bottom
                      aria-pressed={isClearToBottom(display(slot, p.key))}
                      onClick={() => {
                        commitCell(slot, p.key, isClearToBottom(display(slot, p.key)) ? null : CLEAR_TO_BOTTOM);
                        setDraft(null);
                      }}
                      className={cn(
                        'mt-2 min-h-11 rounded-full border px-4 text-sm font-medium touch-manipulation',
                        isClearToBottom(display(slot, p.key))
                          ? 'border-primary-600 bg-primary-600 text-white'
                          : 'border-gray-300 bg-white text-gray-700'
                      )}
                    >
                      Clear to bottom
                    </button>
                  )}
                  {isFailed && !signedOut && (
                    <button
                      type="button"
                      onClick={() => retrySlot(slot)}
                      className="mt-1 flex min-h-11 w-full items-center justify-end gap-1 px-1 text-sm font-semibold text-red-700"
                    >
                      Not saved · Try again
                    </button>
                  )}
                  {isRejected && !isFailed && (
                    <p className="mt-1 px-1 text-right text-sm font-medium text-red-700">
                      Not accepted · check the value
                    </p>
                  )}
                  {refusedNote && (
                    <p role="alert" data-refused-note className="mt-1 px-1 text-sm font-medium text-red-700">
                      {refusedNote}
                    </p>
                  )}
                  {isActive && p.type === 'number' && (
                    <p
                      aria-live="polite"
                      data-decimal-hint
                      className={cn('px-1 text-[13px] font-medium text-amber-800', keyHint?.key === p.key && 'mt-1')}
                    >
                      {keyHint?.key === p.key ? keyHint.text : ''}
                    </p>
                  )}
                  {replaceable && !isRejected && (
                    <p data-replace-hint className="mt-1 px-1 text-[13px] text-gray-600">
                      Typing replaces {text}
                      {p.pairSecondKey && `. Type both numbers, like ${PAIR_EXAMPLE[p.key]}`}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          {notMeasured.length > 0 && (
            <div data-not-measured className="mt-2 space-y-0.5 px-1 text-[13px] text-gray-500">
              {notMeasured.map((p) => (
                <p key={p.key}>{notMeasuredSentence(p, slot)}</p>
              ))}
            </div>
          )}
          <div className="mt-1 flex items-center justify-between px-1 text-[13px] text-gray-600">
            <span data-field-count>
              {filledCount} of {counted.length} field tests for {SLOT_META[slot].label}
            </span>
            <Link
              to="/entry/pond/$pondId/application"
              params={{ pondId }}
              search={{ date }}
              className="flex min-h-11 items-center font-semibold text-primary-600"
            >
              + Log application
            </Link>
          </div>
        </div>
      </div>

      <Keypad
        className="shrink-0 short-landscape:w-[46%] short-landscape:self-stretch short-landscape:overflow-y-auto"
        onDigit={onDigit}
        onDelete={onDelete}
        onClear={onClear}
        digitsDisabled={!active || active.type !== 'number'}
        decimalDisabled={active?.type === 'number' && active.decimals === 0}
        slashKey={!!active?.pairSecondKey}
        secondaryKey={{ label: 'Skip', onPress: onSkip, disabled: !active }}
        actionKey={{ label: 'Next', onPress: onNext, disabled: !active }}
        announcement={announcement}
      />
    </div>
  );
}
